import { postBMICheck } from '@api/index';
import Text from '@components/Text';
import BottomSheet from '@components/_Revamp/BottomSheet';
import Button from '@components/_Revamp/Button';
import Modal from '@components/_Revamp/Modal';
import Stepper from '@components/_Revamp/Stepper';
import { Step } from '@components/_Revamp/Stepper/Stepper';
import { styled } from '@linaria/react';
import { BMICheckRequest } from '@models/BMIRequest.model';
import { HealthDeclarationForm } from '@models/MLTC_R3/HealthDeclaration.model';
import { useAvivaDispatch, useAvivaSelector } from '@redux/hook';
import { updateHealthDeclaration, updateStepCompleted, updateBmiCheckAttempt } from '@redux/slices/MLTC_R3/step3Slice';
import { GAAviva, MLTC_R3_JOURNEY_NAME } from '@utils/gaAvivaR3';
import { Helpers } from '@utils/helpers';
import { get, lowerCase } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { scrollToRef } from '../../../utils/scrollScript';
import { calculateSpacer } from '../../../utils/spaceCalculation';
import { Ending } from './components/Ending/Ending';
import { HeightAndWeight, HeightAndWeightModel } from './components/HeightAndWeight/HeightAndWeight';
import { HelpRequired } from './components/HelpRequired/HelpRequired';
import { IllessSelection } from './components/IllessSelection/IllessSelection';
import { UOB_CONTACT_URL } from '@static/config';



const HealDeclarationContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 327px;
  padding: 0 24px;
  margin: 200px auto 100px auto;
  transition: all 0.2s ease;

  @media screen and (min-width: 768px) {
    max-width: 588px;
    margin: 200px auto 50px auto;
  }
  @media screen and (min-width: 1250px) {
    margin: 150px auto 50px auto;
  }

`;

export const HealthDeclarationMLTCR3 = () => {
  const history = useHistory();
  const dispatch = useAvivaDispatch();

  const basicDetails = useAvivaSelector((state) => state.mltcR3Step1.basicDetails);
  const coverage = useAvivaSelector((state) => state.mltcR3Step1.coverage);
  const healthDeclaration = useAvivaSelector((state) => state.mltcR3Step3.healthDeclaration);
  const bmiCheckAttempt = useAvivaSelector((state) => state.mltcR3Step3.bmiCheckAttempt);  
  const resetQRValues = useAvivaSelector((state) => state.mltcR3Step5.resetQRData);  
  const qrClientDetails = useAvivaSelector(
        (state) => state.mltcR3Step5.qrClientDetails,
      );
  const validQRClient = qrClientDetails?.status === "SUCCESS";
  
  const IllessSelectionRef = useRef<null | HTMLDivElement>(null);
  const QuestionOneRef = useRef<null | HTMLDivElement>(null);
  const EndingRef = useRef<null | HTMLDivElement>(null);
  const SpacerRef = useRef<null | HTMLDivElement>(null);

  const [isHeightAndWeightComplete, setIsHeightAndWeightComplete] = useState(false);
  const [isIllessSelectionComplete, setIsIllessSelectionComplete] = useState(false);
  const [filledForm, setFilledForm] = useState<Set<string>>(new Set());
  const [isCompleteFormFill, setIsCompleteFormFill] = useState(false);
  const [isBMIPass, setBMIPass] = useState<boolean>(false);
  const [values, setValues] = useState<any>(new HealthDeclarationForm(healthDeclaration));
  const [needMoreHelp, setNeedMoreHelp] = useState(false);

  const campaignDetail = useAvivaSelector(state => state.loading.campaignDetail);
  const paramCC = campaignDetail?.campaignCode ? `?cc=${campaignDetail.campaignCode}` : '';
  const combineParamCC = campaignDetail?.campaignCode ? `&cc=${campaignDetail.campaignCode}` : '';
  const analyticUrlParams = useAvivaSelector(state => state.urlAnalytic.param);
  const analyticParam = Helpers.getUrlAnalyticParams(paramCC !== "", analyticUrlParams);

  const [isUOB, setIsUOB] = useState<boolean>(false);
  const [isShowUOB, setIsShowUOB] = useState<boolean>(false);


  // QB-AA-Tracking fix: maps each form name tracked on this page to its journey step.
  const HEALTH_DECLARATION_STEP_INFO: Record<string, { stepName: string; stepNumber: string }> = {
    mltc_health_bmi_form: { stepName: "Health BMI", stepNumber: "7" },
    mltc_health_condition_form: { stepName: "Health Diseases", stepNumber: "8" },
    mltc_health_help_required_form: { stepName: "Health Conditions", stepNumber: "9" },
    mltc_health_eligible_form: { stepName: "Proceed To Purchase", stepNumber: "10" },
  };

  const trackOnLoad = (formName: string): void => {
    const GA = GAAviva.getInstance();
    let { form, page } = GA.digitalData;
    form.name = formName;

    const stepInfo = HEALTH_DECLARATION_STEP_INFO[formName];
    page.journeyName = MLTC_R3_JOURNEY_NAME;
    page.stepName = stepInfo?.stepName || page.stepName;
    page.stepNumber = stepInfo?.stepNumber || page.stepNumber;

    GA.trackPageLoad({ form, page });
  }



  const gotoUOBContact = () => {
    window.open(UOB_CONTACT_URL, "_blank", "noopener noreferrer");
  };


  const onHeightAndWeightComplete = async ({ height, weight }: HeightAndWeightModel) => {
    setValues({ ...values, height, weight });
    console.log("ON HEIGHT AND WEIGHT COMPLETED");
    if (IllessSelectionRef && IllessSelectionRef.current) {
      IllessSelectionRef.current.style.opacity = '1';
      IllessSelectionRef.current.style.display = 'flex';
      let elementOffsetTop =
        IllessSelectionRef.current.offsetTop - parseInt(window.getComputedStyle(IllessSelectionRef.current).getPropertyValue('margin-top'));
      if (!isHeightAndWeightComplete) {
        setIsHeightAndWeightComplete(true);
        calculateSpacer(IllessSelectionRef, SpacerRef, false);
        
      }
      const top = window.pageYOffset || document.documentElement.scrollTop;
      scrollToRef(document.documentElement, 'scrollTop', '', top, elementOffsetTop, 300, true);
    }
    dispatch(updateBmiCheckAttempt());        
    trackOnLoad("mltc_health_condition_form");

  };

  const onIllessSelectionComplete = (illessSelection: string[]) => {
    setValues({ ...values, illessSelection });
    if (QuestionOneRef && QuestionOneRef.current) {
      QuestionOneRef.current.style.opacity = '1';
      QuestionOneRef.current.style.display = 'flex';
      let elementOffsetTop = QuestionOneRef.current.offsetTop - parseInt(window.getComputedStyle(QuestionOneRef.current).getPropertyValue('margin-top'));
      if (!isIllessSelectionComplete) {
        setIsIllessSelectionComplete(true);
        calculateSpacer(QuestionOneRef, SpacerRef, false);
        
      }
      const top = window.pageYOffset || document.documentElement.scrollTop;
      scrollToRef(document.documentElement, 'scrollTop', '', top, elementOffsetTop, 300, true);
    }
    trackOnLoad("mltc_health_help_required_form");
  };

  const onHelpRequiredComplete = (questionName: string, answer: string) => {
    setValues({
      ...values,
      [questionName]: answer,
    });

    let nextRef: React.MutableRefObject<HTMLDivElement | null> = QuestionOneRef;
    nextRef = EndingRef;
    setIsCompleteFormFill(true);

    if (nextRef && nextRef.current) {
      nextRef.current.style.opacity = '1';
      nextRef.current.style.display = 'flex';
      let elementOffsetTop = nextRef.current.offsetTop - parseInt(window.getComputedStyle(nextRef.current).getPropertyValue('margin-top'));
      if (!filledForm.has(questionName)) {
        setFilledForm(filledForm.add(questionName));
        calculateSpacer(nextRef, SpacerRef, false);
      }
      const top = window.pageYOffset || document.documentElement.scrollTop;
      scrollToRef(document.documentElement, 'scrollTop', '', top, elementOffsetTop, 300, true);
    }
    trackOnLoad("mltc_health_eligible_form");
  };

  const onBack = useCallback(() => {

    history.push('/mltc/lets-get-started' + paramCC + analyticParam);
  }, [history]);

  useEffect(() => {
    dispatch(updateHealthDeclaration(values));
  }, [isCompleteFormFill])



  useEffect(() => {
    const checkBmiPassStatus = async () => {
      const { weight, height } = values;
      if (weight && height) {
        if (!(await checkBmi())) {
          console.log("BMI FAILLEEDDD");
          setNeedMoreHelp(true);
        }
        else {
          console.log("BMI PASSSSSSSED");
          setNeedMoreHelp(false);
        }
      }
    }

    if (values.illessSelection!.length || values.q1 === 'Y' || values.q2 === 'Y' || values.q3 === 'Y' || values.q4 === 'Y' || values.httSelection === 'Y') {
      setNeedMoreHelp(true);
    } else {
      setNeedMoreHelp(false);
      checkBmiPassStatus();
    }

  }, [values?.height, values?.weight]);

  useEffect(() => {
    if (values.illessSelection!.length || values.q1 === 'Y' || values.q2 === 'Y' || values.q3 === 'Y' || values.q4 === 'Y' || values.httSelection === 'Y') {
      setNeedMoreHelp(true);
    } else {
      setNeedMoreHelp(false);
    }
  }, [values]);

  useEffect(() => {

    if (!basicDetails) {
      onBack();
      return;
    }

    if (campaignDetail && campaignDetail?.code === 'LUB1') {
      setIsUOB(true);
    }
    if (analyticUrlParams && analyticUrlParams?.ecid === 'LUB1') {
      setIsUOB(true);
    }
    setNeedMoreHelp(false);
    trackOnLoad("mltc_health_bmi_form");

  }, []);

  const checkBmi = useCallback(async () => {
    const { weight, height } = values;
    const dob = basicDetails?.dob;
    const age = Helpers.getAge(new Date(`${dob?.year}-${dob?.month}-${dob?.day}` || ''));
    try {
      const payload = new BMICheckRequest({
        weight,
        height,
        age,
        prodcode: coverage?.plan,
      });
      const response = await postBMICheck(payload);
      const status = get(response, 'data.status', '');
      const bmi = Number(get(response, 'data.bmi', ''));
      console.debug(`QnB ➤ checkBmi() ➤ `, {
        request: payload,
        response,
        status,
        bmi,
        result: lowerCase(status) !== 'decline' && bmi >= 17 && bmi <= 29,
      });
      return lowerCase(status) !== 'decline';
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [values, basicDetails, coverage]);

  const gotoContact = () => {
    if (isUOB) {
      setIsShowUOB(true);
    } else {
      let param = paramCC !== "" ? paramCC + "&variant=true" : "?variant=true";
      history.push('/mltc/contact-form' + param);
    }
  };

  const gotoNextStep = () => {
    history.push('/mltc/my-info' + paramCC + analyticParam);
  };

  const saveStateAndProceed = async () => {
    console.log("saveStateAndProceed called!");
    dispatch(updateStepCompleted(true));
    if (!(await checkBmi()) || needMoreHelp || bmiCheckAttempt >= 4) {
      gotoContact();
      return;
    }
    gotoNextStep();
  };

  const stepsList: Step[] = [
    {
      label: "Basic Info",
      isCurrentStep: false,
      path: "/mltc/lets-get-started",
      disabled: validQRClient,
    },
    {
      label: "Quote",
      isCurrentStep: false,
      path: "/mltc/choose-plan",
    },
    {
      label: "Health Declaration",
      isCurrentStep: true,
      path: "/mltc/health",
    },
    {
      label: "Start Application",
      isCurrentStep: false,
      path: "/mltc/my-info",
    },
    {
      label: "Review & Payment",
      isCurrentStep: false,
      path: "/mltc/review",
    },
  ];

  useEffect(()=>{
    if(resetQRValues){
      setValues({ ...values, height: undefined , weight: undefined });
    }
  },[resetQRValues]);

  return (
    <>
      <HealDeclarationContainer>
        {!isCompleteFormFill && bmiCheckAttempt <= 3 ? (
          <>
            <HeightAndWeight
              onComplete={onHeightAndWeightComplete}
              values={{
                height: values.height!,
                weight: values.weight!,
              }}
            />
            <IllessSelection ref={IllessSelectionRef} onComplete={onIllessSelectionComplete} values={values.illessSelection} />
            <HelpRequired name='q1' ref={QuestionOneRef} onComplete={onHelpRequiredComplete} values={values.q1} />
          </>
        ) : (
          <Ending ref={EndingRef} needMoreHelp={needMoreHelp || bmiCheckAttempt >= 4} onProceedToNextStep={saveStateAndProceed} />
        )}
      </HealDeclarationContainer>

      <div ref={SpacerRef}></div>
      <Stepper steps={stepsList} />
      <Modal show={isShowUOB} onClose={() => { setIsShowUOB(false) }}>
        <Text margin={'0px 0 30px'}>You will be redirected to our appointed intermediary - United Overseas Bank Limited (“UOB”) site to submit the contact me form.</Text>
        <Button margin="0 0 20px" label="Proceed" onClick={gotoUOBContact} />
        <Button variant="secondary" label="Cancel" margin={0} onClick={() => { setIsShowUOB(false) }} />
      </Modal>
      <BottomSheet />

    </>
  );
};


